/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend } from 'recharts';
import ChartContainer from './ChartContainer';
import GlassTooltip from './GlassTooltip';
import { CHART_COLORS } from './chartConstants';

interface NPSGaugeProps {
  score: number;
  promoters?: number;
  passives?: number;
  detractors?: number;
  title?: string;
  className?: string;
}

/**
 * NPS Gauge Component - Displays Net Promoter Score as a semi-circle gauge
 * with distribution breakdown
 */
export const NPSGauge: React.FC<NPSGaugeProps> = ({
  score,
  promoters = 0,
  passives = 0,
  detractors = 0,
  title = 'Net Promoter Score',
  className = ''
}) => {
  // Calculate percentages
  const total = promoters + passives + detractors;
  const promotersPct = total > 0 ? (promoters / total) * 100 : 0;
  const passivesPct = total > 0 ? (passives / total) * 100 : 0;
  const detractorsPct = total > 0 ? (detractors / total) * 100 : 0;

  // Data for the semi-circle gauge
  const gaugeData = [
    { name: 'Detractors', value: detractorsPct, fill: CHART_COLORS.negative },
    { name: 'Passives', value: passivesPct, fill: CHART_COLORS.warning },
    { name: 'Promoters', value: promotersPct, fill: CHART_COLORS.positive }
  ];

  // Get color based on NPS score
  const getScoreColor = (npsScore: number) => {
    if (npsScore >= 50) return CHART_COLORS.positive;
    if (npsScore >= 0) return CHART_COLORS.warning;
    return CHART_COLORS.negative;
  };

  // Get rating text
  const getRating = (npsScore: number) => {
    if (npsScore >= 70) return 'Excellent';
    if (npsScore >= 50) return 'Great';
    if (npsScore >= 30) return 'Good';
    if (npsScore >= 0) return 'Average';
    return 'Needs Improvement';
  };

  return (
    <ChartContainer title={title} className={className}>
      <div className="flex flex-col items-center">
        {/* Semi-circle gauge */}
        <div className="relative w-full" style={{ height: '220px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={gaugeData}
                cx="50%"
                cy="80%"
                startAngle={180}
                endAngle={0}
                innerRadius="60%"
                outerRadius="90%"
                paddingAngle={2}
                dataKey="value"
              >
                {gaugeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>

          {/* Score display in center */}
          <div className="absolute inset-0 flex items-center justify-center" style={{ top: '40%' }}>
            <div className="text-center">
              <div
                className="text-5xl font-bold mb-1"
                style={{ color: getScoreColor(score) }}
              >
                {score}
              </div>
              <div className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
                {getRating(score)}
              </div>
            </div>
          </div>
        </div>

        {/* Distribution breakdown */}
        <div className="w-full mt-6 grid grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold" style={{ color: CHART_COLORS.negative }}>
              {detractorsPct.toFixed(0)}%
            </div>
            <div className="text-xs font-medium mt-1" style={{ color: 'var(--text-muted)' }}>
              Detractors
            </div>
            <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
              {detractors.toLocaleString()}
            </div>
          </div>

          <div className="text-center">
            <div className="text-2xl font-bold" style={{ color: CHART_COLORS.warning }}>
              {passivesPct.toFixed(0)}%
            </div>
            <div className="text-xs font-medium mt-1" style={{ color: 'var(--text-muted)' }}>
              Passives
            </div>
            <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
              {passives.toLocaleString()}
            </div>
          </div>

          <div className="text-center">
            <div className="text-2xl font-bold" style={{ color: CHART_COLORS.positive }}>
              {promotersPct.toFixed(0)}%
            </div>
            <div className="text-xs font-medium mt-1" style={{ color: 'var(--text-muted)' }}>
              Promoters
            </div>
            <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
              {promoters.toLocaleString()}
            </div>
          </div>
        </div>

        {/* Formula explanation */}
        <div
          className="w-full mt-4 p-3 rounded-lg text-xs text-center"
          style={{
            background: 'var(--background-secondary)',
            color: 'var(--text-muted)'
          }}
        >
          NPS = % Promoters - % Detractors
        </div>
      </div>
    </ChartContainer>
  );
};
